import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

import { EnvironmentConfig, IConfiguration } from '../../../../client-config/bma-env-config';
import { AsyncScriptLoaderService } from '../../asyncScriptLoader/async-script-loader.service';
import { IRacingPostGHResponse, IRacingPostHRResponse } from './racing-post.model';

@Injectable({
    providedIn: 'root',
})
export class RacingPostApiService {
    private readonly racingPostApiEndpoint;
    private readonly racingPostOneApiEndpoint;
    private readonly racingPostApiKey;
    private readonly TIMEOUT: number = 10000;
    private readonly environment: IConfiguration = inject(EnvironmentConfig).configuration;

    constructor(
        private http: HttpClient,
        private asyncLoaderService: AsyncScriptLoaderService,
    ) {
        this.racingPostApiEndpoint = this.environment.RACING_POST_API_ENDPOINT;
        this.racingPostOneApiEndpoint = this.environment.RACING_POST_ONE_API_ENDPOINT;
        if (this.asyncLoaderService.isHashIncluded('') && this.environment.ENVIRONMENT!.includes('hlv0')) {
            this.racingPostApiKey =
                this.environment.BRAND == 'ladbrokes' ? 'LD4d09b15b1b304bc5be2534a8bb4a8086' : 'CD996ab523f4c34d569f958607e36374c3';
        } else {
            this.racingPostApiKey = this.environment.RACING_POST_API_KEY;
        }
    }

    /**
     * getHorseRaceDetails
     * @param {string} openBetIds - comma-separated string
     * @returns {Observable<IRacingPostHRResponse>}
     */
    getHorseRaceDetails(openBetIds: string): Observable<IRacingPostHRResponse> {
        if (!openBetIds) {
            return of({} as IRacingPostHRResponse);
        } else {
            const url = `${this.racingPostApiEndpoint}/categories/21/events/${openBetIds}/content?locale=en-GB&api-key=${this.racingPostApiKey}`;
            return this.performRequest(url);
        }
    }

    /**
     * getHorseRaceDetailsFromOne-Api
     * @param {string} openBetIds - comma-separated string
     * @returns {Observable<IRacingPostHRResponse>}
     */
    getHorseRaceOneApiResultDetails(openBetIds: string): Observable<IRacingPostHRResponse> {
        if (!openBetIds) {
            return of({} as IRacingPostHRResponse);
        } else {
            const url = `${this.racingPostOneApiEndpoint}/categories/21/events/${openBetIds}/content?locale=en-GB&api-key=${this.racingPostApiKey}`;
            return this.performRequest(url);
        }
    }

    /**
     * getGreyhoundRaceDetails
     * @param {string} openBetIds - comma-separated string
     * @returns {Observable<IRacingPostHRResponse>}
     */
    getGreyhoundRaceDetails(openBetIds: string): Observable<IRacingPostGHResponse> {
        if (!openBetIds) {
            return of({} as IRacingPostGHResponse);
        } else {
            const url = `${this.racingPostApiEndpoint}/categories/19/events/${openBetIds}/content?locale=en-GB&api-key=${this.racingPostApiKey}`;
            return this.performRequest(url);
        }
    }

    /**
     * getGreyhoundRaceDetailsFromOne-Api
     * @param {string} openBetIds - comma-separated string
     * @returns {Observable<IRacingPostGHResponse>}
     */
    getGreyhoundRaceOneApiResultDetails(openBetIds: string): Observable<IRacingPostGHResponse> {
        if (!openBetIds) {
            return of({} as IRacingPostGHResponse);
        } else {
            const url = `${this.racingPostOneApiEndpoint}/categories/19/events/${openBetIds}/content?locale=en-GB&api-key=${this.racingPostApiKey}`;
            return this.performRequest(url);
        }
    }

    /**
     * PerformRequest()
     * @param url
     * @param params
     * @returns {Observable<HttpResponse<T>>}
     */
    private performRequest<T>(url: string, params: any = {}): Observable<T> {
        return this.http
            .get<T>(`${url}`, {
                observe: 'response',
                params: params,
                headers: {
                    accept: 'application/json',
                },
            })
            .pipe(
                timeout(this.TIMEOUT),
                map((data: HttpResponse<T>) => data.body),
                catchError(() => {
                    return of(<any>{});
                }),
            );
    }
}
